import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  FileText,
  Loader2,
  Mail,
  Receipt,
  Ticket,
  Wallet,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { PatientTabFrame } from '@/components/patient/PatientTabFrame';
import { AppointmentDetailsModal } from '@/pages/appointments/AppointmentDetailsModal';
import { AppointmentModal } from '@/pages/appointments/AppointmentModal';
import { useDebouncedActivitySearch, usePatientActivity } from '@/hooks/usePatientActivity';
import { useQuestionnaireNameMap } from '@/hooks/useQuestionnaireNameMap';
import { resolveQuestionnaireDisplayName } from '@/lib/questionnaireDisplayName';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import {
  EMPTY_ACTIVITY_FILTERS,
  activityActionLabel,
  activityDetail,
  activityEmptyKind,
  activityNavigation,
  activityTitle,
  countActiveActivityFilters,
  formatActivityDayHeading,
  formatActivityTime,
  groupActivityByDay,
  type ActivityCategoryFilter,
  type ActivityEmptyKind,
  type ActivityFilterState,
  type PatientActivityItem,
} from '@/lib/patientActivity';
import { resolveJournalDateRange, type JournalDatePreset } from '@/utils/journalHistoryFilters';
import { ActivityFilters } from './ActivityFilters';

interface ActivityTabProps {
  patientId: string;
  clinicId: string;
}

function activityEmptyCopy(
  t: (key: string, options?: Record<string, unknown>) => string,
  term: string,
  kind: ActivityEmptyKind,
): { title: string; description: string } {
  switch (kind) {
    case 'search':
      return {
        title: t('patients.activity.empty_search_title', { term }),
        description: t('patients.activity.empty_search_description'),
      };
    case 'filtered':
      return {
        title: t('patients.activity.empty_filtered_title'),
        description: t('patients.activity.empty_filtered_description'),
      };
    case 'none':
      return {
        title: t('patients.activity.empty_title'),
        description: t('patients.activity.empty_description'),
      };
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

const CATEGORY_ICON: Record<ActivityCategoryFilter, typeof Calendar> = {
  appointments: Calendar,
  journal: FileText,
  invoices: Receipt,
  payments: Wallet,
  communications: Mail,
  klippekort: Ticket,
};

function toQueryRange(filters: ActivityFilterState): { from?: string; to?: string } {
  const range = resolveJournalDateRange(
    filters.datePreset as JournalDatePreset,
    filters.customFrom,
    filters.customTo,
    new Date(),
  );
  return {
    from: range.from ? new Date(`${range.from}T00:00:00`).toISOString() : undefined,
    to: range.to
      ? new Date(new Date(`${range.to}T00:00:00`).getTime() + 24 * 60 * 60 * 1000).toISOString()
      : undefined,
  };
}

export function ActivityTab({ patientId, clinicId }: ActivityTabProps) {
  const { t, i18n } = useTranslation();
  const { map: templateNames } = useQuestionnaireNameMap(clinicId, i18n.language);
  const unnamed = t('forms.unnamed_questionnaire', 'Spørgeskema');
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<ActivityFilterState>(EMPTY_ACTIVITY_FILTERS);
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const debouncedSearch = useDebouncedActivitySearch(search);
  const range = useMemo(() => toQueryRange(filters), [filters]);
  const query = usePatientActivity(clinicId, patientId, {
    category: filters.category,
    search: debouncedSearch,
    from: range.from,
    to: range.to,
  });
  const refreshing = query.isFetching && !query.isLoading && !query.isFetchingNextPage;
  const settledQuery = {
    search: debouncedSearch.trim(),
    filtersActive: countActiveActivityFilters(filters) > 0,
  };
  const [shownQuery, setShownQuery] = useState(settledQuery);
  if (
    !query.isLoading &&
    !query.isPlaceholderData &&
    (shownQuery.search !== settledQuery.search ||
      shownQuery.filtersActive !== settledQuery.filtersActive)
  ) {
    setShownQuery(settledQuery);
  }

  const events = useMemo(() => {
    const raw = query.data?.pages.flat() ?? [];
    return raw.map((event) => {
      if (event.event_type !== 'questionnaire_sent') return event;
      const rawName = event.payload.questionnaire_name ?? '';
      const name = resolveQuestionnaireDisplayName({
        templateId: rawName,
        snapshotName: rawName,
        locale: i18n.language,
        templatesById: templateNames,
        fallback: unnamed,
      });
      if (name === rawName) return event;
      return { ...event, payload: { ...event.payload, questionnaire_name: name } };
    });
  }, [query.data, templateNames, i18n.language, unnamed]);
  const groups = useMemo(() => groupActivityByDay(events), [events]);

  const openEvent = (event: PatientActivityItem) => {
    const target = activityNavigation(event, patientId);
    if (!target) return;
    if (target.kind === 'appointment') {
      setDetailsId(target.appointmentId);
      return;
    }
    navigate(target.path);
  };

  return (
    <>
    <PatientTabFrame
      title={t('patients.tabs.activity')}
      count={events.length}
      loading={query.isLoading}
      error={query.isError ? t('patients.activity.error') : undefined}
      onRetry={() => void query.refetch()}
      toolbar={
        <div className="mt-s4">
        <ActivityFilters
          search={search}
          onSearchChange={setSearch}
          filters={filters}
          onFiltersChange={setFilters}
        />
        {refreshing ? (
          <p
            role="status"
            className={cn(TYPE.small, 'mt-s2 flex items-center gap-s2 text-ds-muted')}
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            {t('patients.activity.refreshing')}
          </p>
        ) : null}
        </div>
      }
      empty={
        !query.isLoading && !query.isError && events.length === 0
          ? {
              icon: <FileText className="h-7 w-7" />,
              ...activityEmptyCopy(
                t,
                shownQuery.search,
                activityEmptyKind(shownQuery.search, shownQuery.filtersActive),
              ),
            }
          : undefined
      }
    >
        <div className="mt-s4 space-y-s5" aria-busy={refreshing || undefined}>
          {groups.map((group) => (
            <div key={group.dayKey}>
              <h3 className={cn(TYPE.label, 'mb-s2 text-ds-muted')}>
                {formatActivityDayHeading(group.dayKey, t, i18n.language)}
              </h3>
              <ol className="divide-y divide-ds-line">
                {group.events.map((event) => {
                  const Icon = CATEGORY_ICON[event.category];
                  const detail = activityDetail(event, t);
                  const action = activityActionLabel(event, t);
                  const target = activityNavigation(event, patientId);
                  return (
                    <li key={event.id}>
                      <button
                        type="button"
                        disabled={!target}
                        onClick={() => openEvent(event)}
                        className={cn(
                          'flex w-full items-start gap-s3 py-s3 text-left',
                          target && 'hover:bg-ds-sunk',
                          !target && 'cursor-default',
                        )}
                      >
                        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ds-teal-tint text-ds-teal">
                          <Icon className="h-4 w-4" aria-hidden />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className={cn('block text-ds-text', TYPE.body)}>
                            <span className="text-ds-muted">
                              {formatActivityTime(event.occurred_at)}
                            </span>
                            {' · '}
                            {activityTitle(event, t)}
                          </span>
                          {detail ? (
                            <span className={cn(TYPE.small, 'mt-0.5 block text-ds-muted')}>{detail}</span>
                          ) : null}
                          {action ? (
                            <span className={cn(TYPE.small, 'mt-s1 block text-ds-teal')}>{action}</span>
                          ) : null}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </div>
          ))}

          {query.hasNextPage ? (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                disabled={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {query.isFetchingNextPage
                  ? t('patients.activity.loading_more', 'Loading…')
                  : t('patients.activity.load_more', 'Load more')}
              </Button>
            </div>
          ) : null}
        </div>
    </PatientTabFrame>

      {detailsId && (
        <AppointmentDetailsModal
          appointmentId={detailsId}
          clinicId={clinicId}
          onClose={() => setDetailsId(null)}
          onEdit={() => {
            const appointmentId = detailsId;
            setDetailsId(null);
            setEditingId(appointmentId);
          }}
        />
      )}
      {editingId && (
        <AppointmentModal
          clinicId={clinicId}
          mode="edit"
          appointmentId={editingId}
          onClose={() => setEditingId(null)}
        />
      )}
    </>
  );
}
